import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../config/axiosInterceptor";

const ASSET_TYPES = [
  { value: "frames", label: "Frames" },
  { value: "frameLevel", label: "Frame Level" },
  { value: "entryEffectLevel", label: "Entry Effect Level" },
  { value: "bubbles", label: "Bubbles" },
  { value: "profileBadge", label: "Profile Badge" },
  { value: "userBadge", label: "User Badge" },
  { value: "namePlate", label: "Name Plate" },
  { value: "vip", label: "VIP" },
  { value: "entryeffects", label: "Entry Effects" },
  { value: "wallpaper", label: "Wallpaper / Theme" },
  { value: "level", label: "Level" },
  { value: "tags", label: "Tags" },
  { value: "familypk", label: "Family PK" },
  { value: "familyImage", label: "Family Image" },
  { value: "gifts", label: "Gifts" },
  { value: "gif", label: "GIFs" },
  { value: "livegifts", label: "Live Gifts" },
  { value: "banner", label: "Banner / Ad / Event" },
];

// Which fetch strategies each upload strategy can serve. You can only read from
// a backend the upload strategy actually writes to: "S3 only" uploads served
// "from Server" would point every new asset at a server that never receives the
// file. Mirrors allowedFetchByUpload in
// backend-ms/controllers/admin/assetStorageConfig.js, which enforces the same
// rule server-side - keep the two in step.
const UPLOAD_FETCH_MATRIX = {
  storedInServer: ["fetchFromServer"],
  storeInS3: ["fetchFromS3"],
  storeInMinio: ["fetchFromMinio"],
  storeBoth: ["fetchFromServer", "fetchFromS3"],
  storeInS3AndMinio: ["fetchFromS3", "fetchFromMinio"],
  storeAll: ["fetchFromServer", "fetchFromS3", "fetchFromMinio"],
};

const FETCH_LABELS = {
  fetchFromServer: "Fetch from Server (Direct URL)",
  fetchFromS3: "Fetch from S3 (Signed URL)",
  fetchFromMinio: "Fetch from MinIO (CDN URL)",
};

const emptyConfig = {
  uploadStrategy: "storedInServer",
  fetchStrategy: "fetchFromServer",
  s3Config: {
    bucketName: "",
    region: "",
    cloudfrontUrl: "",
    hasAccessKeyId: false,
    hasSecretAccessKey: false,
  },
  minioConfig: {
    endpoint: "",
    port: 443,
    useSSL: true,
    bucketName: "",
    cdnUrl: "",
    hasAccessKey: false,
    hasSecretKey: false,
  },
};

const AssetStorageConfig = () => {
  const [assetType, setAssetType] = useState("frames");
  const [config, setConfig] = useState(emptyConfig);
  // Secret inputs are tracked separately from `config` since the GET response
  // never contains raw secret values - only typed-in changes get submitted.
  const [secrets, setSecrets] = useState({
    accessKeyId: "",
    secretAccessKey: "",
    accessKey: "",
    secretKey: "",
  });

  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingMinio, setTestingMinio] = useState(false);
  const [copyingFromVideo, setCopyingFromVideo] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showMinioPassword, setShowMinioPassword] = useState(false);

  // Save opens a confirm dialog listing which asset types to write to.
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveTargets, setSaveTargets] = useState([]);

  useEffect(() => {
    fetchConfig(assetType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetType]);

  const fetchConfig = async (type) => {
    try {
      setLoading(true);
      setSecrets({ accessKeyId: "", secretAccessKey: "", accessKey: "", secretKey: "" });
      const response = await axiosInstance.post(`/admin/asset-storage-config/${type}`);
      if (response.data.success === 1) {
        const loaded = response.data.details;
        // A config saved before the upload/fetch pairing was enforced can hold a
        // combination the Fetch dropdown no longer offers, which would render it
        // blank. Show the nearest valid option and say so, rather than silently
        // submitting a value the admin never picked.
        const allowed = UPLOAD_FETCH_MATRIX[loaded.uploadStrategy] || [];
        if (allowed.length && !allowed.includes(loaded.fetchStrategy)) {
          toast.warn(
            `Saved fetch strategy "${loaded.fetchStrategy}" is not valid for this upload ` +
              `strategy; showing "${FETCH_LABELS[allowed[0]]}". Save to apply.`
          );
          loaded.fetchStrategy = allowed[0];
        }
        setConfig(loaded);
      } else {
        toast.error(response.data.message || "Failed to fetch configuration");
      }
    } catch (error) {
      toast.error("Failed to fetch configuration");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const needsS3 =
      config.uploadStrategy === "storeInS3" ||
      config.uploadStrategy === "storeBoth" ||
      config.uploadStrategy === "storeInS3AndMinio" ||
      config.uploadStrategy === "storeAll" ||
      config.fetchStrategy === "fetchFromS3";

    const needsMinio =
      config.uploadStrategy === "storeInMinio" ||
      config.uploadStrategy === "storeInS3AndMinio" ||
      config.uploadStrategy === "storeAll" ||
      config.fetchStrategy === "fetchFromMinio";

    if (
      needsS3 &&
      (!config.s3Config.bucketName ||
        !config.s3Config.region ||
        (!config.s3Config.hasAccessKeyId && !secrets.accessKeyId) ||
        (!config.s3Config.hasSecretAccessKey && !secrets.secretAccessKey))
    ) {
      toast.error("Please fill all S3 configuration fields when using S3 storage");
      return;
    }

    if (
      needsMinio &&
      (!config.minioConfig.endpoint ||
        !config.minioConfig.bucketName ||
        (!config.minioConfig.hasAccessKey && !secrets.accessKey) ||
        (!config.minioConfig.hasSecretKey && !secrets.secretKey))
    ) {
      toast.error("Please fill all MinIO configuration fields when using MinIO storage");
      return;
    }

    // Validation passed - ask which asset types these settings should go to.
    setSaveTargets([assetType]);
    setShowSaveDialog(true);
  };

  const toggleSaveTarget = (type) => {
    // The asset type being edited is always a target and can't be unticked.
    if (type === assetType) return;
    setSaveTargets((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const confirmSave = async () => {
    try {
      setLoading(true);
      const payload = {
        assetTypes: saveTargets,
        uploadStrategy: config.uploadStrategy,
        fetchStrategy: config.fetchStrategy,
        s3Config: {
          bucketName: config.s3Config.bucketName,
          region: config.s3Config.region,
          cloudfrontUrl: config.s3Config.cloudfrontUrl,
          ...(secrets.accessKeyId ? { accessKeyId: secrets.accessKeyId } : {}),
          ...(secrets.secretAccessKey ? { secretAccessKey: secrets.secretAccessKey } : {}),
        },
        minioConfig: {
          endpoint: config.minioConfig.endpoint,
          port: config.minioConfig.port,
          useSSL: config.minioConfig.useSSL,
          bucketName: config.minioConfig.bucketName,
          cdnUrl: config.minioConfig.cdnUrl,
          ...(secrets.accessKey ? { accessKey: secrets.accessKey } : {}),
          ...(secrets.secretKey ? { secretKey: secrets.secretKey } : {}),
        },
      };

      const response = await axiosInstance.post(
        `/admin/asset-storage-config/${assetType}/apply-to-many`,
        payload
      );
      if (response.data.success === 1) {
        toast.success(response.data.message || "Configuration updated successfully");
        setConfig(response.data.details);
        setSecrets({ accessKeyId: "", secretAccessKey: "", accessKey: "", secretKey: "" });
        setShowSaveDialog(false);
      } else {
        toast.error(response.data.message || "Failed to update configuration");
      }
    } catch (error) {
      toast.error("Failed to update configuration");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const testS3Connection = async () => {
    try {
      setTesting(true);
      const response = await axiosInstance.post(`/admin/asset-storage-config/${assetType}/test-s3`);
      if (response.data.success === 1) {
        toast.success("S3 connection successful!");
      } else {
        toast.error(response.data.message || "S3 connection failed");
      }
    } catch (error) {
      toast.error("S3 connection test failed");
      console.error(error);
    } finally {
      setTesting(false);
    }
  };

  // Prefills the form with the Video Storage Config's credentials. Nothing is
  // persisted here - the admin reviews the values and clicks Save Configuration.
  // Deliberately leaves uploadStrategy/fetchStrategy alone: those are the
  // admin's choice for this asset type and have nothing to do with credentials.
  const copyFromVideoConfig = async ({ copyS3 = false, copyMinio = false }) => {
    try {
      setCopyingFromVideo(true);
      const response = await axiosInstance.post(
        `/admin/asset-storage-config/${assetType}/copy-from-video`,
        { copyS3, copyMinio }
      );
      if (response.data.success === 1) {
        const { s3Config, minioConfig } = response.data.details;

        setConfig((prev) => ({
          ...prev,
          ...(s3Config && {
            s3Config: {
              ...prev.s3Config,
              bucketName: s3Config.bucketName || "",
              region: s3Config.region || "",
              cloudfrontUrl: s3Config.cloudfrontUrl || "",
              hasAccessKeyId: !!s3Config.accessKeyId,
              hasSecretAccessKey: !!s3Config.secretAccessKey,
            },
          }),
          ...(minioConfig && {
            minioConfig: {
              ...prev.minioConfig,
              endpoint: minioConfig.endpoint || "",
              port: minioConfig.port ?? 443,
              useSSL: minioConfig.useSSL ?? true,
              bucketName: minioConfig.bucketName || "",
              cdnUrl: minioConfig.cdnUrl || "",
              hasAccessKey: !!minioConfig.accessKey,
              hasSecretKey: !!minioConfig.secretKey,
            },
          }),
        }));

        // Put the secrets into the visible inputs so Save submits them.
        setSecrets((prev) => ({
          ...prev,
          ...(s3Config && {
            accessKeyId: s3Config.accessKeyId || "",
            secretAccessKey: s3Config.secretAccessKey || "",
          }),
          ...(minioConfig && {
            accessKey: minioConfig.accessKey || "",
            secretKey: minioConfig.secretKey || "",
          }),
        }));

        toast.success(response.data.message || "Loaded from Video Storage Config");
      } else {
        toast.error(response.data.message || "Failed to load from Video Storage Config");
      }
    } catch (error) {
      toast.error("Failed to load from Video Storage Config");
      console.error(error);
    } finally {
      setCopyingFromVideo(false);
    }
  };

  const testMinioConnection = async () => {
    try {
      setTestingMinio(true);
      const response = await axiosInstance.post(`/admin/asset-storage-config/${assetType}/test-minio`);
      if (response.data.success === 1) {
        toast.success("MinIO connection successful!");
      } else {
        toast.error(response.data.message || "MinIO connection failed");
      }
    } catch (error) {
      toast.error("MinIO connection test failed");
      console.error(error);
    } finally {
      setTestingMinio(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig((prev) => {
      const next = { ...prev, [field]: value };

      // Changing the upload strategy can invalidate the saved fetch strategy
      // (e.g. Server+S3 -> S3 only leaves "Fetch from Server" selected, which
      // the backend rejects). Snap it to the first still-valid option rather
      // than letting the form hold a combination that cannot be saved.
      if (field === "uploadStrategy") {
        const allowed = UPLOAD_FETCH_MATRIX[value] || [];
        if (allowed.length && !allowed.includes(next.fetchStrategy)) {
          next.fetchStrategy = allowed[0];
        }
      }

      return next;
    });
  };

  const handleS3ConfigChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      s3Config: {
        ...prev.s3Config,
        [field]: value,
      },
    }));
  };

  const handleMinioConfigChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      minioConfig: {
        ...prev.minioConfig,
        [field]: value,
      },
    }));
  };

  const handleSecretChange = (field, value) => {
    setSecrets((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading && !config._id) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">Asset Storage Configuration</h4>
              <small>Manage upload and fetch strategies for admin-managed assets</small>
            </div>
            <div className="card-body">
              <div className="form-group mb-4">
                <label className="font-weight-bold">
                  <i className="fas fa-layer-group mr-2"></i>
                  Asset Type
                </label>
                <select
                  className="form-control"
                  value={assetType}
                  onChange={(e) => setAssetType(e.target.value)}
                >
                  {ASSET_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <small className="form-text text-muted">
                  Each asset type has its own independent storage configuration.
                </small>
              </div>

              <hr />

              <form onSubmit={handleSubmit}>
                {/* Upload Strategy */}
                <div className="form-group mb-4">
                  <label className="font-weight-bold">
                    <i className="fas fa-upload mr-2"></i>
                    Upload Strategy
                  </label>
                  <select
                    className="form-control"
                    value={config.uploadStrategy}
                    onChange={(e) => handleInputChange("uploadStrategy", e.target.value)}
                    required
                  >
                    <option value="storedInServer">Store in Server Only (Local Storage)</option>
                    <option value="storeInS3">Store in S3 Only (AWS S3 Bucket)</option>
                    <option value="storeInMinio">Store in MinIO Only (MinIO Object Storage)</option>
                    <option value="storeBoth">Store in Both Server & S3</option>
                    <option value="storeInS3AndMinio">Store in S3 & MinIO (Multi-Cloud)</option>
                    <option value="storeAll">Store in All (Server + S3 + MinIO)</option>
                  </select>
                  <small className="form-text text-muted">
                    Choose where new uploads for this asset type will be stored.
                  </small>
                </div>

                {/* Fetch Strategy */}
                <div className="form-group mb-4">
                  <label className="font-weight-bold">
                    <i className="fas fa-download mr-2"></i>
                    Fetch Strategy
                  </label>
                  <select
                    className="form-control"
                    value={config.fetchStrategy}
                    onChange={(e) => handleInputChange("fetchStrategy", e.target.value)}
                    required
                  >
                    {(UPLOAD_FETCH_MATRIX[config.uploadStrategy] || []).map((f) => (
                      <option key={f} value={f}>
                        {FETCH_LABELS[f]}
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted">
                    Choose how this asset type's files will be fetched when displaying.
                    {(UPLOAD_FETCH_MATRIX[config.uploadStrategy] || []).length === 1
                      ? " Only one option is valid for the selected upload strategy."
                      : " Limited to the backends the selected upload strategy writes to."}
                  </small>
                </div>

                {/* S3 Configuration Section */}
                {(config.uploadStrategy === "storeInS3" ||
                  config.uploadStrategy === "storeBoth" ||
                  config.uploadStrategy === "storeInS3AndMinio" ||
                  config.uploadStrategy === "storeAll" ||
                  config.fetchStrategy === "fetchFromS3") && (
                  <div className="card bg-light mb-4">
                    <div className="card-header bg-info text-white">
                      <h5 className="mb-0">
                        <i className="fab fa-aws mr-2"></i>
                        AWS S3 Configuration
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Bucket Name *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={config.s3Config.bucketName}
                              onChange={(e) => handleS3ConfigChange("bucketName", e.target.value)}
                              placeholder="my-asset-bucket"
                              required={config.uploadStrategy === "storeInS3"}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Region *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={config.s3Config.region}
                              onChange={(e) => handleS3ConfigChange("region", e.target.value)}
                              placeholder="us-east-1"
                              required={config.uploadStrategy === "storeInS3"}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Access Key ID *</label>
                            <input
                              type="text"
                              className="form-control"
                              name="asset-s3-access-key-id"
                              autoComplete="off"
                              value={secrets.accessKeyId}
                              onChange={(e) => handleSecretChange("accessKeyId", e.target.value)}
                              placeholder={
                                config.s3Config.hasAccessKeyId
                                  ? "•••••••• (leave blank to keep current)"
                                  : "AKIAIOSFODNN7EXAMPLE"
                              }
                              required={config.uploadStrategy === "storeInS3" && !config.s3Config.hasAccessKeyId}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Secret Access Key *</label>
                            <div className="input-group">
                              <input
                                type={showPassword ? "text" : "password"}
                                className="form-control"
                                name="asset-s3-secret-access-key"
                                autoComplete="new-password"
                                value={secrets.secretAccessKey}
                                onChange={(e) => handleSecretChange("secretAccessKey", e.target.value)}
                                placeholder={
                                  config.s3Config.hasSecretAccessKey
                                    ? "•••••••• (leave blank to keep current)"
                                    : "wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                                }
                                required={
                                  config.uploadStrategy === "storeInS3" && !config.s3Config.hasSecretAccessKey
                                }
                              />
                              <div className="input-group-append">
                                <button
                                  className="btn btn-outline-secondary"
                                  type="button"
                                  onClick={() => setShowPassword(!showPassword)}
                                >
                                  <i className={`fas fa-eye${showPassword ? "-slash" : ""}`}></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>
                          CloudFront URL (Optional)
                          <i
                            className="fas fa-info-circle ml-2"
                            title="Use CloudFront CDN for faster asset delivery"
                          ></i>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={config.s3Config.cloudfrontUrl}
                          onChange={(e) => handleS3ConfigChange("cloudfrontUrl", e.target.value)}
                          placeholder="https://d111111abcdef8.cloudfront.net"
                        />
                        <small className="form-text text-muted">
                          If you have CloudFront CDN configured, enter the distribution URL here for faster delivery
                        </small>
                      </div>

                      <button
                        type="button"
                        className="btn btn-info btn-sm"
                        onClick={testS3Connection}
                        disabled={testing}
                      >
                        {testing ? (
                          <>
                            <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                            Testing Connection...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check-circle mr-2"></i>
                            Test S3 Connection
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm ml-2"
                        onClick={() => copyFromVideoConfig({ copyS3: true })}
                        disabled={copyingFromVideo}
                        title="Fill these fields with the S3 bucket/region/keys from Video Storage Config. Nothing is saved until you click Save Configuration."
                      >
                        {copyingFromVideo ? (
                          <>
                            <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                            Loading...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-copy mr-2"></i>
                            Copy S3 config from Video
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* MinIO Configuration Section */}
                {(config.uploadStrategy === "storeInMinio" ||
                  config.uploadStrategy === "storeInS3AndMinio" ||
                  config.uploadStrategy === "storeAll" ||
                  config.fetchStrategy === "fetchFromMinio") && (
                  <div className="card bg-light mb-4">
                    <div className="card-header bg-success text-white">
                      <h5 className="mb-0">
                        <i className="fas fa-database mr-2"></i>
                        MinIO Object Storage Configuration
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Endpoint *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={config.minioConfig?.endpoint || ""}
                              onChange={(e) => handleMinioConfigChange("endpoint", e.target.value)}
                              placeholder="gcdn.ahamcloudservices.com"
                              required={config.uploadStrategy === "storeInMinio"}
                            />
                            <small className="form-text text-muted">MinIO server endpoint (without http://)</small>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>Port</label>
                            <input
                              type="number"
                              className="form-control"
                              value={config.minioConfig?.port || 443}
                              onChange={(e) => handleMinioConfigChange("port", parseInt(e.target.value))}
                              placeholder="443"
                            />
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>Use SSL</label>
                            <select
                              className="form-control"
                              value={config.minioConfig?.useSSL ? "true" : "false"}
                              onChange={(e) => handleMinioConfigChange("useSSL", e.target.value === "true")}
                            >
                              <option value="true">Yes (HTTPS)</option>
                              <option value="false">No (HTTP)</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Bucket Name *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={config.minioConfig?.bucketName || ""}
                              onChange={(e) => handleMinioConfigChange("bucketName", e.target.value)}
                              placeholder="masti"
                              required={config.uploadStrategy === "storeInMinio"}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>CDN URL (Optional)</label>
                            <input
                              type="text"
                              className="form-control"
                              value={config.minioConfig?.cdnUrl || ""}
                              onChange={(e) => handleMinioConfigChange("cdnUrl", e.target.value)}
                              placeholder="https://cdn.yoursite.com"
                            />
                            <small className="form-text text-muted">Custom CDN URL if configured</small>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Access Key *</label>
                            <input
                              type="text"
                              className="form-control"
                              name="asset-minio-access-key"
                              autoComplete="off"
                              value={secrets.accessKey}
                              onChange={(e) => handleSecretChange("accessKey", e.target.value)}
                              placeholder={
                                config.minioConfig.hasAccessKey
                                  ? "•••••••• (leave blank to keep current)"
                                  : "image-api-user"
                              }
                              required={config.uploadStrategy === "storeInMinio" && !config.minioConfig.hasAccessKey}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Secret Key *</label>
                            <div className="input-group">
                              <input
                                type={showMinioPassword ? "text" : "password"}
                                className="form-control"
                                name="asset-minio-secret-key"
                                autoComplete="new-password"
                                value={secrets.secretKey}
                                onChange={(e) => handleSecretChange("secretKey", e.target.value)}
                                placeholder={
                                  config.minioConfig.hasSecretKey
                                    ? "•••••••• (leave blank to keep current)"
                                    : "Your secret key"
                                }
                                required={
                                  config.uploadStrategy === "storeInMinio" && !config.minioConfig.hasSecretKey
                                }
                              />
                              <div className="input-group-append">
                                <button
                                  className="btn btn-outline-secondary"
                                  type="button"
                                  onClick={() => setShowMinioPassword(!showMinioPassword)}
                                >
                                  <i className={`fas fa-eye${showMinioPassword ? "-slash" : ""}`}></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={testMinioConnection}
                        disabled={testingMinio}
                      >
                        {testingMinio ? (
                          <>
                            <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                            Testing Connection...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check-circle mr-2"></i>
                            Test MinIO Connection
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm ml-2"
                        onClick={() => copyFromVideoConfig({ copyMinio: true })}
                        disabled={copyingFromVideo}
                        title="Fill these fields with the MinIO endpoint/keys from Video Storage Config. Nothing is saved until you click Save Configuration."
                      >
                        {copyingFromVideo ? (
                          <>
                            <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                            Loading...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-copy mr-2"></i>
                            Copy MinIO config from Video
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Information Alert */}
                <div className="alert alert-info">
                  <h6 className="alert-heading">
                    <i className="fas fa-info-circle mr-2"></i>
                    Configuration Guide
                  </h6>
                  <ul className="mb-0">
                    <li><strong>Independent per asset type:</strong> Each asset type (Frames, VIP, Bubbles, etc.) has its own storage settings - changing one does not affect the others, or the Video Storage Config.</li>
                    <li><strong>Store in Server Only:</strong> Files stored locally on your server</li>
                    <li><strong>Store in S3 Only:</strong> Files uploaded to AWS S3 bucket only</li>
                    <li><strong>Store in MinIO Only:</strong> Files uploaded to MinIO object storage only</li>
                    <li><strong>Store in Both (Server & S3):</strong> Files stored in both server and S3</li>
                    <li><strong>Store in S3 & MinIO:</strong> Files stored in both cloud storage providers (multi-cloud redundancy)</li>
                    <li><strong>Store in All:</strong> Files stored in Server + S3 + MinIO (maximum redundancy)</li>
                    <li><strong>Fetch from Server:</strong> All files for this asset type served directly from your server</li>
                    <li><strong>Fetch from S3:</strong> All files served via signed URLs from S3 or CloudFront CDN</li>
                    <li><strong>Fetch from MinIO:</strong> All files served from MinIO or custom CDN</li>
                    <li><strong>Secrets:</strong> For security, saved access keys are never shown - leave the field blank to keep the current value, or type a new one to replace it.</li>
                    <li><strong>Copy from Video:</strong> Already have working S3/MinIO credentials in Video Storage Config? The "Copy from Video" button fills those fields in for you. It only fills the form - your Upload/Fetch Strategy choices are left alone, and nothing is applied until you click Save Configuration.</li>
                  </ul>
                </div>

                {/* Submit Button */}
                <div className="form-group mb-0">
                  <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save mr-2"></i>
                        Save Configuration
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-lg ml-2"
                    onClick={() => fetchConfig(assetType)}
                    disabled={loading}
                  >
                    <i className="fas fa-sync-alt mr-2"></i>
                    Refresh
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {showSaveDialog && (
        <>
          <div className="modal d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-save mr-2"></i>
                    Save these settings to:
                  </h5>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setShowSaveDialog(false)}
                    disabled={loading}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <p className="text-muted">
                    Tick every asset type that should get these exact settings. Leaving a
                    secret field blank on the form is fine - the saved credentials from{" "}
                    <strong>{ASSET_TYPES.find((t) => t.value === assetType)?.label}</strong>{" "}
                    are applied to all of them.
                  </p>

                  <div className="mb-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm mr-2"
                      onClick={() => setSaveTargets(ASSET_TYPES.map((t) => t.value))}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setSaveTargets([assetType])}
                    >
                      Clear others
                    </button>
                  </div>

                  <div className="row">
                    {ASSET_TYPES.map((t) => (
                      <div className="col-md-4" key={t.value}>
                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`save-target-${t.value}`}
                            checked={saveTargets.includes(t.value)}
                            disabled={t.value === assetType}
                            onChange={() => toggleSaveTarget(t.value)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`save-target-${t.value}`}
                          >
                            {t.label}
                            {t.value === assetType && (
                              <span className="badge badge-info ml-1">current</span>
                            )}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>

                  {saveTargets.length > 1 && (
                    <div className="alert alert-warning mt-3 mb-0">
                      <i className="fas fa-exclamation-triangle mr-2"></i>
                      This overwrites the existing storage settings on{" "}
                      <strong>{saveTargets.length}</strong> asset types.
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowSaveDialog(false)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={confirmSave}
                    disabled={loading || saveTargets.length === 0}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check mr-2"></i>
                        Save to {saveTargets.length} type{saveTargets.length === 1 ? "" : "s"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </>
      )}
    </div>
  );
};

export default AssetStorageConfig;
